import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { CanonicalTag } from '@/components/CanonicalTag';

export default function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isValidSession, setIsValidSession] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();
  const validRef = useRef(false);

  useEffect(() => {
    const markValid = () => {
      validRef.current = true;
      setIsValidSession(true);
      setCheckingSession(false);
      toast.success('You can now set your new password');
    };

    // Listen for PASSWORD_RECOVERY event from the URL hash token
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'PASSWORD_RECOVERY' && session) {
          markValid();
        } else if (event === 'SIGNED_IN' && session && !validRef.current) {
          markValid();
        }
      }
    );

    // Also check for existing session (e.g. page refresh after recovery)
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && session.user && !validRef.current) {
        markValid();
      }
    });

    // Timeout fallback — if no auth event fires within 5 seconds, redirect
    const timeout = setTimeout(() => {
      if (!validRef.current) {
        setCheckingSession(false);
        toast.error('Invalid or expired reset link');
        setTimeout(() => navigate('/auth'), 2000);
      }
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, [navigate, isValidSession]);

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 12) {
      toast.error('Password must be at least 12 characters long');
      return;
    }

    setLoading(true);

    try {
      // Check session first
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        toast.error('Session expired. Please request a new reset link');
        navigate('/auth');
        return;
      }

      const { error } = await supabase.auth.updateUser({ 
        password: password 
      });

      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Password updated successfully!');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (error: any) {
      toast.error(`An unexpected error occurred: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="/triathlon-finish-background.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'hsl(var(--background) / 0.80)' }} />
        </div>
        <Card className="relative w-full max-w-md shadow-modal">
          <CardContent className="flex items-center justify-center p-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!isValidSession) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img 
            src="/triathlon-finish-background.jpg"
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0" style={{ backgroundColor: 'hsl(var(--background) / 0.80)' }} />
        </div>
        <Card className="relative w-full max-w-md shadow-modal">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground">Redirecting to login...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen flex items-center justify-center">
      {/* Subtle branded background */}
      <div className="absolute inset-0">
        <img 
          src="/triathlon-finish-background.jpg"
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0" style={{ backgroundColor: 'hsl(var(--background) / 0.80)' }}  />
      </div>
      
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>Reset Password | Sink Bike Run</title>
      </Helmet>
      <CanonicalTag url="/reset-password" />
      <Card className="relative w-full max-w-md shadow-modal">
        <CardHeader className="space-y-1 text-center">
          <CardTitle className="text-2xl font-light tracking-tight bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent">
            SINK BIKE RUN
          </CardTitle>
          <CardDescription>
            Set your new password for your triathlon companion
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={12}
                  placeholder="Enter new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={12}
                  placeholder="Confirm new password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              variant="accent"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Updating Password...
                </>
              ) : (
                'Update Password'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}